import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type Company, type Partner, type TaxationForm, type ZusScheme } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { date as written } from '../format';
import * as Icon from './Icons';

/**
 * Kartoteka wspólników spółki osobowej (E18-S2).
 *
 * Wpis ma **okres**, a nie stan na dziś: zaliczkę za marzec liczy się według udziałów z marca,
 * więc wspólnika się nie kasuje — kończy się jego udział i zaczyna nowy wiersz. Suma udziałów stoi
 * na wierzchu, bo to jedyna liczba, po której widać, że komuś zapomniano dopisać części.
 */
export function PartnersPanel({
  company,
  t,
  locale,
  editable,
}: {
  company: Company;
  t: Dictionary;
  locale: Locale;
  editable: boolean;
}) {
  const [partners, setPartners] = useState<Partner[] | null>(null);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    share: '',
    taxationForm: 'SKALA' as TaxationForm,
    zusScheme: 'PELNY' as ZusScheme,
    fromDate: `${new Date().getFullYear()}-01-01`,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setPartners(await api.partners(company.id));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    }
  }, [company.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  const active = (partners ?? []).filter((partner) => partner.toDate === null);
  const sum = active.reduce((total, partner) => total + Number(partner.profitShare), 0);

  return (
    <div className="panel">
      <header>
        <h4>{t.settings.partners.title}</h4>
        {active.length > 0 && (
          <div className="sp">
            <span className={`chip ${Math.abs(sum - 1) < 0.0001 ? 'st-ok' : 'st-warn'}`}>
              <i />
              {t.settings.partners.sum}: {(sum * 100).toFixed(2)}%
            </span>
          </div>
        )}
      </header>
      <p className="hint">{t.settings.partners.lead}</p>

      {error && (
        <div className="alert a-bad">
          <Icon.Error />
          <div>
            <b>{error}</b>
          </div>
        </div>
      )}

      {partners?.length === 0 ? (
        <p className="hint">{t.settings.partners.none}</p>
      ) : (
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>{t.settings.partners.lastName}</th>
                <th className="n">{t.settings.partners.share}</th>
                <th>{t.settings.partners.taxation}</th>
                <th>{t.settings.partners.from}</th>
                <th>{t.settings.partners.to}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(partners ?? []).map((partner) => (
                <tr key={partner.id}>
                  <td>
                    {partner.firstName} {partner.lastName}
                  </td>
                  <td className="n mono">{(Number(partner.profitShare) * 100).toFixed(2)}%</td>
                  <td className="muted sm">
                    {t.settings.forms[partner.taxationForm.toLowerCase() as keyof Dictionary['settings']['forms']]}
                  </td>
                  <td className="doc">{written(partner.fromDate, locale)}</td>
                  <td className="doc">{partner.toDate ? written(partner.toDate, locale) : '—'}</td>
                  <td className="act">
                    {editable && partner.toDate === null && (
                      <button
                        type="button"
                        className="btn ghost sm"
                        disabled={busy}
                        onClick={() => {
                          const day = window.prompt(t.settings.partners.endPrompt, partner.fromDate);
                          if (day) void run(() => api.endPartner(company.id, partner.id, day));
                        }}
                      >
                        {t.settings.partners.end}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editable && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              await api.addPartner(company.id, {
                firstName: form.firstName,
                lastName: form.lastName,
                // „50%" i „0,5" znaczą to samo — człowiek nie ma się zastanawiać, której formy chcemy
                profitShare: shareOf(form.share),
                taxationForm: form.taxationForm,
                zusScheme: form.zusScheme,
                fromDate: form.fromDate,
              });
              setForm({ ...form, firstName: '', lastName: '', share: '' });
            });
          }}
        >
          <div className="fields">
            <div className="field">
              <label htmlFor="pFirst">{t.settings.partners.firstName}</label>
              <input
                id="pFirst"
                className="inp"
                value={form.firstName}
                onChange={(event) => setForm({ ...form, firstName: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="pLast">{t.settings.partners.lastName}</label>
              <input
                id="pLast"
                className="inp"
                value={form.lastName}
                onChange={(event) => setForm({ ...form, lastName: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="pShare">{t.settings.partners.share}</label>
              <input
                id="pShare"
                className="inp mono"
                inputMode="decimal"
                value={form.share}
                onChange={(event) => setForm({ ...form, share: event.target.value })}
                required
              />
              <span className="hint">{t.settings.partners.shareHint}</span>
            </div>
            <div className="field">
              <label htmlFor="pTax">{t.settings.partners.taxation}</label>
              <select
                id="pTax"
                className="inp"
                value={form.taxationForm}
                onChange={(event) => setForm({ ...form, taxationForm: event.target.value as TaxationForm })}
              >
                {(['SKALA', 'LINIOWY', 'RYCZALT'] as const).map((value) => (
                  <option key={value} value={value}>
                    {t.settings.forms[value.toLowerCase() as keyof Dictionary['settings']['forms']]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="pZus">{t.settings.partners.zus}</label>
              <select
                id="pZus"
                className="inp"
                value={form.zusScheme}
                onChange={(event) => setForm({ ...form, zusScheme: event.target.value as ZusScheme })}
              >
                {(['ULGA_NA_START', 'PREFERENCYJNY', 'MALY_ZUS_PLUS', 'PELNY', 'BRAK'] as const).map((value) => (
                  <option key={value} value={value}>
                    {t.register.settlements.zusSchemes[value]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="pFrom">{t.settings.partners.from}</label>
              <input
                id="pFrom"
                className="inp"
                type="date"
                value={form.fromDate}
                onChange={(event) => setForm({ ...form, fromDate: event.target.value })}
                required
              />
            </div>
          </div>
          <div className="bar2">
            <button type="submit" className="btn sec" disabled={busy}>
              <Icon.Plus />
              {t.settings.partners.add}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/** „50%", „50" i „0,5" to ten sam udział — zamieniamy je na ułamek, którego oczekuje serwer. */
function shareOf(value: string): string {
  const text = value.replace(',', '.').replace('%', '').trim();
  const number = Number(text);
  if (!Number.isFinite(number) || number <= 0) return '0';
  return (number > 1 ? number / 100 : number).toFixed(6);
}
